import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Button, Card, Space, Layout, message, Typography, Spin, Segmented } from 'antd';
import { useNavigate } from 'react-router';
import { userApi } from '../api/user';
import { LoginRequest } from '../api/user';
import { getPasswordHash } from '../util/crypto';
import { wechatScanLoginApi } from '../api/wechat-scan-login';
import { ReloadOutlined, CheckCircleOutlined, QrcodeOutlined, KeyOutlined } from '@ant-design/icons';
import { useCountdown, useInterval } from '../util/hooks';

interface LoginFormValues {
  username: string;
  password: string;
}

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

interface LoginComponentProps {
  visible: boolean;
  onFinishLogin: () => void;
}

const WechatLogin: React.FC<LoginComponentProps> = ({ visible, onFinishLogin }) => {
  const finishLoginCallbackRef = useRef<() => void>(onFinishLogin);
  finishLoginCallbackRef.current = onFinishLogin;

  const [wechatLoading, setWechatLoading] = useState(false);
  const [scanToken, setScanToken] = useState<string>('');
  const [qrCodeBase64, setQrCodeBase64] = useState<string>('');
  const [qrCodeStatus, setQrCodeStatus] = useState<'normal' | 'scanned' | 'expired' | 'success'>('normal');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [enablePolling, setEnablePolling] = useState<boolean>(false);
  const navigate = useNavigate();

  const [refreshCountdown, refreshEnabled, resetRefreshCountdown] = useCountdown(5, false);

  const poll = useCallback(() => {
    console.log('checkTokenState', scanToken);
    wechatScanLoginApi.checkTokenState({ token: scanToken })
      .then(response => {
        if (response.success && response.data) {
          const { is_valid, state } = response.data;
          
          if (!is_valid) {
            // 二维码已失效
            setQrCodeStatus('expired');
            setStatusMessage('二维码已失效');
            setEnablePolling(false);
          } else if (state === 1) {
            // 已扫码，请在手机上确认
            setQrCodeStatus('scanned');
            setStatusMessage('已扫码，请在手机上确认');
          } else if (state === 2) {
            // 登录成功
            setQrCodeStatus('success');
            setStatusMessage('登录成功');
            setEnablePolling(false);
            finishLoginCallbackRef.current?.();
          }
        }
      })
    .catch(error => {
      console.error('Check token state error:', error);
    });
  }, [scanToken, navigate]);

  useInterval(poll, 1000, enablePolling);

  // 启动轮询
  const startPolling = useCallback((token: string) => {
    setScanToken(token);
    setEnablePolling(true);
  }, []);

  // 获取二维码
  const getQRCode = useCallback(async () => {
    console.log('getQRCode', refreshCountdown, refreshEnabled);
    if (!refreshEnabled) {
      message.warning('请5秒后再刷新二维码');
      return;
    }
    setWechatLoading(true);
    try {
      const response = await wechatScanLoginApi.getQRCode('doctor');
      if (response.success && response.data) {
        setQrCodeBase64(response.data.qr_code_base64);
        setQrCodeStatus('normal');
        setStatusMessage('');
        
        // 启动轮询
        resetRefreshCountdown();
        startPolling(response.data.token);
      } else {
        message.error('获取二维码失败：' + (response.message || '未知错误'));
      }
    } catch (error) {
      console.error('Get QR code error:', error);
      message.error('获取二维码失败，请稍后重试');
    } finally {
      setWechatLoading(false);
    }
  }, [refreshEnabled, startPolling]);

  useEffect(() => {
    getQRCode();
  }, []);

  return (
    <div style={{ textAlign: 'center' }} hidden={!visible}>
      <Spin spinning={wechatLoading}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          {/* 二维码 */}
          <div style={{ width: 200, height: 200, margin: '0 auto' }}>
            {qrCodeBase64 && (
              <img 
                src={qrCodeBase64} 
                alt="微信扫码登录" 
                style={{ width: '100%', height: '100%' }}
              />
            )}
          </div>

          {/* 状态遮罩 */}
          {(qrCodeStatus === 'scanned' || qrCodeStatus === 'expired' || qrCodeStatus === 'success') && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 8
            }}>
              {qrCodeStatus === 'scanned' && (
                <>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 10 }} />
                  <Text style={{ color: '#52c41a', fontSize: 16 }}>{statusMessage}</Text>
                </>
              )}
              {qrCodeStatus === 'expired' && (
                <Text style={{ color: '#666', fontSize: 16 }}>{statusMessage}</Text>
              )}
              {qrCodeStatus === 'success' && (
                <>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 10 }} />
                  <Text style={{ color: '#52c41a', fontSize: 16 }}>{statusMessage}</Text>
                </>
              )}
            </div>
          )}
        </div>

        {/* 刷新按钮 */}
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button 
            type="link" 
            icon={<ReloadOutlined />} 
            onClick={getQRCode} 
            disabled={!refreshEnabled || wechatLoading}
          >
            刷新二维码
          </Button>
          {!refreshEnabled && (
            <Text style={{ color: '#999', marginLeft: 10 }}>
              {refreshCountdown}秒后可刷新
            </Text>
          )}
        </div>
      </Spin>
    </div>
  );
}

const UserPassLogin: React.FC<LoginComponentProps> = ({ visible, onFinishLogin }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const finishLoginCallbackRef = useRef<() => void>(onFinishLogin);
  finishLoginCallbackRef.current = onFinishLogin;

  // 用户名密码登录
  const onFinish = useCallback(async (values: LoginFormValues) => {
    setLoading(true);
    try {
      const passwordHash = await getPasswordHash(values.password);
      const loginData: LoginRequest = {
        user_type: 'doctor',
        username: values.username,
        password_hash: passwordHash
      };
      const response = await userApi.login(loginData);
      if (response.success) {
        finishLoginCallbackRef.current?.();
      } else {
        message.error(response.message || '登录失败，请检查用户名和密码');
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  const onFinishFailed = useCallback((errorInfo: any) => {
    console.log('Failed:', errorInfo);
    message.error('表单验证失败，请检查输入');
  }, []);

  return (
    <Form<LoginFormValues>
      name="basic"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      initialValues={{ remember: true }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      hidden={!visible}
    >
      <Form.Item
        label="用户名"
        name="username"
        rules={[{ required: true, message: '请输入您的用户名!' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="密码"
        name="password"
        rules={[{ required: true, message: '请输入您的密码!' }]}
      >
        <Input.Password />
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            登录
          </Button>
          <Button type="link" onClick={() => navigate('/register')}>
            注册账号
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}

const Login: React.FC = () => {
  const [loginMode, setLoginMode] = useState<'scan' | 'password'>('scan');
  const navigate = useNavigate();
  const onFinishLogin = useCallback(() => {
    message.success('登录成功');
    // 1秒后重定向到首页
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  }, [navigate]);

  return (
    <Layout className="layout" style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', textAlign: 'center', padding: 0 }}>
        <h1>医生管理系统</h1>
      </Header>
      <Content style={{ padding: '0 50px' }}>
        <div style={{ 
          background: '#fff', 
          padding: 24, 
          marginTop: 24, 
          minHeight: 280, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          <Card title="医生登录" style={{ width: 400 }}>
            {/* 登录方式切换 */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 20 }}>
              <Segmented<"scan" | "password">
                options={[
                  { label: '微信登录', value: 'scan', icon: <QrcodeOutlined /> },
                  { label: '密码登录', value: 'password', icon: <KeyOutlined /> },
                ]}
                value={loginMode}
                onChange={value => setLoginMode(value)}
              />
            </div>

            {/* 微信扫码登录 */}
            <WechatLogin visible={loginMode === 'scan'} onFinishLogin={onFinishLogin} />
            {/* 用户名密码登录 */}
            <UserPassLogin visible={loginMode === 'password'} onFinishLogin={onFinishLogin} />
          </Card>
        </div>
      </Content>
      <Footer style={{ textAlign: 'center' }}>医生管理系统 ©{new Date().getFullYear()}</Footer>
    </Layout>
  );
};

export default Login;